import React, { useEffect, useRef, useState } from 'react';
import { fetchFromPublicAPI } from '../services/common.js';
import { setInvoiceSaveOnPay } from '../services/paymentsService.js';

/**
 * VirtualTerminalForm
 * ===================
 *
 * Staff-facing "virtual terminal": lets the office key a card manually
 * and take a payment against a specific invoice. This is the manual-entry
 * counterpart to pushing the invoice to a physical terminal.
 *
 * How it works
 *   - Mounts the Rainforest Payment Component bound to the invoice's
 *     existing ``payin_config_id`` (the same config the public invoice
 *     page charges against). With the built-in pay button visible, the
 *     element performs the actual payin when staff submits.
 *   - "Save this card" is checked by default for a patient invoice and
 *     locked off for an insurance one (the carrier's card is never saved).
 *     The box drives ``invoice.saveOnPayPreference``, which the payin /
 *     payment_method webhooks read, so the card is saved only when it is
 *     ticked. The card details live only inside Rainforest's secure
 *     iframe; we never see the PAN/CVV.
 *   - On the ``approved`` event the payin is submitted; settlement is
 *     finalised asynchronously by the payin webhook, so the parent shows
 *     its existing "waiting for payment" modal (card-on-file style).
 *
 * Props
 *   invoice  — the invoice being charged. Must carry ``publicHash`` and a
 *              payin config (``payinConfigData.payin_config_id`` or
 *              ``payinConfigId``).
 *   onPaid() — fires after Rainforest reports the payin approved.
 *   onCancel() — fires when staff dismisses the form.
 */
export default function VirtualTerminalForm({ invoice, onPaid, onCancel }) {
    const [sessionKey, setSessionKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const componentRef = useRef(null);
    const isInsurance = invoice?.invoiceType === 'insurance';
    const [saveCard, setSaveCard] = useState(!isInsurance);
    const [saveSyncing, setSaveSyncing] = useState(false);

    const hash = invoice?.publicHash;

    // Keep the invoice's save-on-pay in step with the box. On failure the box
    // goes back to what the invoice still says.
    const toggleSaveCard = async (next) => {
        if (isInsurance) return;
        const prev = saveCard;
        setSaveCard(next);
        setSaveSyncing(true);
        try {
            const res = await setInvoiceSaveOnPay(hash, next);
            if (!res?.ok) throw new Error(res?.data?.error || 'save-on-pay update failed');
        } catch (err) {
            console.warn('[VirtualTerminal] could not update save-on-pay preference', err);
            setSaveCard(prev);
            setError('Could not update the "Save this card" choice. Please try again.');
        } finally {
            setSaveSyncing(false);
        }
    };
    // The public invoice GET flattens this to ``payinConfigId``; the raw
    // staff invoice doc only carries the nested ``payinConfigData``. Accept
    // either so this works no matter which shape the parent passes.
    const payinConfigId =
        invoice?.payinConfigId || invoice?.payinConfigData?.payin_config_id || null;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);

            if (!hash) {
                setError('This invoice has no public link, so it cannot be charged here.');
                setLoading(false);
                return;
            }
            if (!payinConfigId) {
                setError('This invoice has no payment configuration. It may not have a merchant set up.');
                setLoading(false);
                return;
            }

            // Start the invoice at the box's default: save for a patient
            // invoice, never for insurance (the server refuses insurance
            // saves regardless). Best-effort so staff can still take the
            // payment, but log it.
            try {
                await setInvoiceSaveOnPay(hash, !isInsurance);
            } catch (err) {
                console.warn('[VirtualTerminal] could not set save-on-pay preference', err);
            }

            let data;
            try {
                data = await fetchFromPublicAPI(`/api/payments/get-invoice-session/${hash}`);
            } catch (err) {
                if (cancelled) return;
                setError('Could not load the secure card form. Please try again.');
                setLoading(false);
                return;
            }
            if (cancelled) return;
            if (!data?.success || !data?.session_key) {
                setError(data?.error || 'Could not load the secure card form.');
                setLoading(false);
                return;
            }
            setSessionKey(data.session_key);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [hash, payinConfigId, isInsurance]);

    useEffect(() => {
        if (!sessionKey || !componentRef.current) return undefined;
        const el = componentRef.current;

        const handleApproved = () => {
            onPaid?.();
        };
        const handleDeclined = () => {
            setError('The card was declined. Please try a different card.');
        };
        const handleError = () => {
            setError('There was a problem processing the card. Please try again.');
        };

        el.addEventListener('approved', handleApproved);
        el.addEventListener('declined', handleDeclined);
        el.addEventListener('error', handleError);

        return () => {
            el.removeEventListener('approved', handleApproved);
            el.removeEventListener('declined', handleDeclined);
            el.removeEventListener('error', handleError);
        };
    }, [sessionKey, onPaid]);

    return (
        <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e3e7e4',
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 12,
            }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Virtual Terminal — enter card to charge</div>
                <button
                    type="button"
                    onClick={onCancel}
                    style={{
                        padding: '4px 10px',
                        backgroundColor: '#fff',
                        color: '#6b716d',
                        border: '1px solid #cdd3cf',
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: 'pointer',
                    }}
                >
                    Back
                </button>
            </div>

            <div style={{
                fontSize: 12,
                color: '#6b716d',
                marginBottom: 10,
                lineHeight: 1.4,
            }}>
                Card details are entered directly into NewtonPay's secure form — we
                never see the card number or CVV.
            </div>

            <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12,
                cursor: isInsurance ? 'not-allowed' : 'pointer',
                opacity: isInsurance ? 0.55 : 1,
            }}>
                <input
                    type="checkbox"
                    checked={saveCard && !isInsurance}
                    disabled={isInsurance || saveSyncing || !hash}
                    onChange={(e) => toggleSaveCard(e.target.checked)}
                    style={{ marginTop: 3, cursor: isInsurance ? 'not-allowed' : 'pointer' }}
                />
                <span>
                    <span style={{ fontSize: 14, color: '#1a1d1b', fontWeight: 600 }}>Save this card for future payments</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#6b716d' }}>
                        {isInsurance
                            ? 'Insurance payment: the insurance company’s card will not be saved. Insurance cards are never added to a patient’s card on file.'
                            : 'Securely store the card so the patient can pay future invoices in one click.'}
                    </span>
                </span>
            </label>

            {loading && (
                <div style={{ color: '#6b716d', fontSize: 13 }}>Loading secure card form…</div>
            )}

            {!loading && sessionKey && payinConfigId && (
                // Keyed on sessionKey so a fresh session always remounts a
                // clean iframe instead of React reusing one with stale state.
                <rainforest-payment
                    key={sessionKey}
                    ref={componentRef}
                    session-key={sessionKey}
                    payin-config-id={payinConfigId}
                    allowed-methods="CARD"
                ></rainforest-payment>
            )}

            {error && (
                <div style={{ color: '#b42318', fontSize: 13, marginTop: 8 }}>{error}</div>
            )}
        </div>
    );
}
